import { languageOptions } from 'components/forms/onboardingInputs/workHistory/data';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import intersectionBy from 'lodash/intersectionBy';
import { GeographyOptionResource } from 'util/resources/GeographyOption';
import { LANGUAGES } from 'util/constants/languages';
import cloneDeep from 'lodash/cloneDeep';
import groupBy from 'lodash/groupBy';
import {
  SearchJobFilterOptionType,
  SearchJobFilterType,
} from 'features/SearchJobsFilter/SearchJobFilterType';

const checkboxLangOptions = languageOptions.map((opt) => {
  return {
    ...opt,
    checked: false,
  };
});

export type JobApplicationCheckboxFilterOption = {
  value: string;
  label: string;
  subLabel?: string;
  checked?: boolean;
  geography?: string;
  geographyId?: string;
  group?: string;
  selected?: boolean;
  defaultValue?: boolean;
};

export type JobApplicationDistanceFilterValue = {
  lat: number;
  lng: number;
  radiusInMiles?: number;
  formattedAddress: string;
  placeId: string;
};

export type JobApplicationsFilter = {
  id: string;
  name: string;
  type: string;
  placeholder?: string;
  value?: string | object | JobApplicationDistanceFilterValue;
  options?: JobApplicationCheckboxFilterOption[];
  groupedOptions?:
    | JobApplicationCheckboxFilterOption[]
    | { [key: string]: JobApplicationCheckboxFilterOption[] };
  hasGeoLabel?: boolean;
  hasGroupLabel?: boolean;
  getValue?: (
    value?: string | object | JobApplicationDistanceFilterValue,
  ) => Partial<JobApplicationDistanceFilterValue>;
  checkAllAbove?: boolean;
};

export type AppliedFilter = {
  section: JobApplicationsFilter;
  option: JobApplicationCheckboxFilterOption;
  value?: string | object;
};

export type TeacherAppliedFilter = {
  section: SearchJobFilterType;
  option: SearchJobFilterOptionType;
  value?: string | object;
};

export type ApplicationDropdownFilterOptions = {
  id: string;
  label: string;
  value: string;
  selected: boolean;
  defaultValue?: boolean;
};

export const filters: JobApplicationsFilter[] = [
  {
    id: 'query',
    name: 'Text Search',
    type: 'text',
    placeholder: 'Search by name...',
    value: '',
  },
  {
    id: 'distance',
    name: 'Distance',
    type: 'complex',
    value: {
      lat: undefined,
      lng: undefined,
      radiusInMiles: undefined,
    },
    getValue: (value: JobApplicationDistanceFilterValue): JobApplicationDistanceFilterValue => {
      return value.placeId
        ? {
            lat: value?.lat,
            lng: value?.lng,
            radiusInMiles: value?.radiusInMiles,
            formattedAddress: value?.formattedAddress,
            placeId: value?.placeId,
          }
        : undefined;
    },
  },
  {
    id: 'video_ask_answers',
    name: 'Video Interview',
    type: 'checkbox',
    options: [{ value: 'completed', label: 'Video Interview Completed', checked: false }],
  },
  {
    id: 'job_search_status',
    name: 'Job Search Status',
    type: 'checkbox',
    options: [
      { value: 'I am actively looking for a new role', label: 'Actively Looking', checked: false },
      {
        value:
          'I am NOT actively looking for a new role, but am open to connecting with potential employers',
        label: 'Not actively looking but open to connecting',
        checked: false,
      },
      {
        value: 'I am NOT actively looking for a new role',
        label: 'Not actively looking',
        checked: false,
      },
    ],
  },
  {
    id: 'employment_type',
    name: 'Desired Schedule',
    type: 'checkbox',
    options: [
      { value: 'Full-time', label: 'Full Time', checked: false },
      { value: 'Part-time', label: 'Part Time', checked: false },
      { value: 'Seasonal', label: 'Seasonal', checked: false },
      { value: 'Substitute', label: 'Substitute', checked: false },
    ],
  },
  {
    id: 'role_type',
    name: 'Desired Role',
    type: 'checkbox',
    hasGeoLabel: true,
    options: [],
  },
  {
    id: 'education_level',
    name: 'Minimum Education',
    type: 'checkbox',
    checkAllAbove: true,
    options: [
      { value: '0', label: 'High School Diploma or equivalent', checked: false },
      { value: '1', label: 'Associate Degree', checked: false },
      { value: '2', label: "Bachelor's Degree", checked: false },
      { value: '3', label: "Master's Degree", checked: false },
      { value: '4', label: 'Doctorate Degree', checked: false },
    ],
  },
  {
    id: 'by_major_in',
    name: 'Major/Degree',
    type: 'checkbox',
    options: [
      {
        label: 'Early Childhood Education or a Related Field',
        value: 'early_childhood',
        checked: false,
      },
    ],
  },
  {
    id: 'work_experience',
    name: 'Work Experience',
    type: 'checkbox',
    options: [
      {
        label: 'Any experience working with children',
        value: 'any_experience_with_children',
        checked: false,
      },
      {
        label: 'Preschool, Childcare Center, Daycare',
        value: encodeURIComponent('Preschool, Childcare Center, Daycare'),
        checked: false,
      },
      { label: 'Family Childcare Home', value: 'Family Childcare Home', checked: false },
      {
        label: 'Private Home or Individual Family (as a Nanny or Babysitter)',
        value: 'Private Home or Individual Family (as a Nanny or Babysitter)',
        checked: false,
      },
      {
        label: 'Before or Afterschool Program',
        value: 'Before or Afterschool Program',
        checked: false,
      },
      {
        label: 'K-12th grade (Elementary, Middle or High School)',
        value: encodeURIComponent('K-12th grade (Elementary, Middle or High School)'),
        checked: false,
      },
      { label: 'Tutoring Center', value: 'Tutoring Center', checked: false },
      { label: 'Camp', value: 'Camp', checked: false },
    ],
  },
  {
    id: 'desired_location',
    name: 'Location Preference',
    type: 'checkbox',
    hasGeoLabel: true,
    options: [],
  },
  {
    id: 'certifications',
    name: 'Teaching Credential and Licenses',
    type: 'checkbox',
    hasGeoLabel: true,
    hasGroupLabel: true,
    options: [],
  },
  {
    id: 'education_philosophy',
    name: 'Educational Philosophy',
    hasGeoLabel: true,
    type: 'checkbox',
    options: [],
  },
  {
    id: 'languages',
    name: 'Additional Languages',
    type: 'checkbox',
    options: map(LANGUAGES, (language) => ({ value: language, label: language })),
  },
];

export const dropdownFilters = [
  {
    id: 'all-educators',
    label: 'All Educators',
    value: '',
    selected: true,
  },
  {
    id: 'recommended',
    label: 'Recommended',
    value: 'recommended',
  },
  {
    id: 'liked',
    label: 'Liked Educators',
    value: 'liked_by_employer',
  },
  {
    id: 'previously-applied',
    label: 'Previously Applied',
    value: 'previously_applied',
  },
  {
    id: 'messaged',
    label: 'Previously Messaged',
    value: 'messaged',
  },
  {
    id: 'unviewed',
    label: 'Unviewed Educators',
    value: 'not_viewed_by_employer',
  },
  {
    id: 'viewed',
    label: 'Viewed Educators',
    value: 'viewed_by_employer',
  },
];

export const applicationsDropdownFilters = {
  id: 'show_status',
  name: 'Show Status',
  type: 'dropdown',
  options: [
    {
      id: 'all-educators',
      label: 'All Educators',
      value: '',
      defaultValue: true,
      selected: true,
    },
    {
      id: 'liked',
      label: 'Liked Educators',
      value: 'liked_by_employer',
      selected: false,
    },
    {
      id: 'previously_messaged',
      label: 'Previously Sent Message',
      value: 'previously_messaged',
      selected: false,
    },
    {
      id: 'viewed',
      label: 'Viewed Applicants',
      value: 'job_application_viewed_by_employer',
      selected: false,
    },
    {
      id: 'unviewed',
      label: 'Unviewed Applicants',
      value: 'job_application_not_viewed_by_employer',
      selected: false,
    },
    {
      id: 'interview_invite_sent',
      label: 'Interview Invite Sent',
      value: 'interview_invite_sent',
      selected: false,
    },
    {
      id: 'has_interviews',
      label: 'Interview Scheduled',
      value: 'has_interviews',
      selected: false,
    },
    {
      id: 'has_notes',
      label: 'Internal Notes Added',
      value: 'has_notes',
      selected: false,
    },
  ] as ApplicationDropdownFilterOptions[],
};

export const checkboxFiltersGenerator = (
  filters: JobApplicationsFilter[],
  geographyId: string,
): JobApplicationsFilter[] => {
  return cloneDeep(filters)
    .filter((filter) => filter.type === 'checkbox' && filter.options.length)
    .map((filter) => {
      let groupedOptions:
        | JobApplicationCheckboxFilterOption[]
        | { [key: string]: JobApplicationCheckboxFilterOption[] } = filter.options;
      if (!geographyId && filter.hasGeoLabel) {
        groupedOptions = groupBy(groupedOptions, 'geography');
      }

      if (filter.hasGroupLabel) {
        if (!geographyId && filter.hasGeoLabel) {
          Object.keys(groupedOptions).forEach((geo) => {
            groupedOptions[geo] = groupBy(groupedOptions[geo], 'group');
          });
        } else {
          groupedOptions = groupBy(groupedOptions, 'group') as {
            [key: string]: JobApplicationCheckboxFilterOption[];
          };
        }
      }

      filter.groupedOptions = groupedOptions;
      return filter;
    });
};

export const filtersTransformer = (
  payload: GeographyOptionResource[],
  filters: JobApplicationsFilter[],
) => {
  const eduPhilosophy = payload
    .filter((phi) => phi.category === 'filterable_education_philosophy')
    .map((phi) => {
      return {
        id: phi.id,
        geographyId: phi.geographyId,
        geography: phi.geography()?.longName,
        value: phi.title,
        label: phi.title,
      };
    });

  const sortedEduPhilosophy = sortBy(eduPhilosophy, ['geography', 'label']);
  const desiredEduPhilFilter = filters.find((filter) => filter.id === 'education_philosophy');

  desiredEduPhilFilter.options = desiredEduPhilFilter.options.length
    ? intersectionBy(desiredEduPhilFilter.options, sortedEduPhilosophy, 'id')
    : sortedEduPhilosophy;

  const desiredRoles = payload
    .filter((role) => role.category === 'filterable_desired_role')
    .map((role) => {
      return {
        id: role.id,
        geographyId: role.geographyId,
        geography: role.geography()?.longName,
        value: role.title,
        label: role.title,
      };
    });

  const sortedDesiredRoles = sortBy(desiredRoles, ['geography', 'label']);
  const desiredRoleFilter = filters.find((filter) => filter.id === 'role_type');

  desiredRoleFilter.options = desiredRoleFilter.options.length
    ? intersectionBy(desiredRoleFilter.options, sortedDesiredRoles, 'id')
    : sortedDesiredRoles;

  const locations = payload
    .filter(
      (location) =>
        location.category === 'location' && location.geography()?.displayNeighborhoodLocations,
    )
    .map((location) => {
      return {
        id: location.id,
        geographyId: location.geographyId,
        geography: location.geography()?.longName,
        value: location.id,
        label: location.title,
      };
    });

  const sortedLocations = sortBy(locations, ['geography', 'label']);
  const locationFilter = filters.find((filter) => filter.id === 'desired_location');

  locationFilter.options = locationFilter.options.length
    ? intersectionBy(locationFilter.options, sortedLocations, 'id')
    : sortedLocations;

  return filters;
};
